import { useState, useEffect, useMemo, useCallback } from 'react'
import { Row, Col, FormText, Input, InputGroup, Label, FormFeedback } from 'reactstrap'

import type { ColorType } from 'api/types'

import { ColorPicker } from 'components/common/ColorPicker/ColorPicker'
import { SelectBox } from 'components/common/FormFormat/SelectBox'
import type { ColumnSize } from 'components/common/types'
import { getUUID } from 'components/common/utils'

import { COLUMN_SIZES } from '../constants'

import type { ChangeEvent, FocusEvent } from 'react'

type FormProps = {
  label: string | JSX.Element
  labelSize?: ColumnSize
  size?: ColumnSize
  placeholder?: string
  formText?: string | JSX.Element
  className?: string
}

type InputFormProps = FormProps & {
  value?: string
  type?: 'text' | 'email' | 'password'
  maxLength?: number
  disabled?: boolean
  autoComplete?: string
  onChange?: (value: string) => void
  validations?: Array<(s: string | undefined) => string>
  onValidate?: (b: boolean) => void
}

type SelectBoxFormProps = FormProps & {
  value?: string
  displayValue?: string
  items: Array<{ key?: string | number; value: string }>
  disabled?: boolean
  onChange: (item: { key?: string | number; value: string }) => void
  validations?: Array<(s: string | undefined) => string>
  onValidate?: (b: boolean) => void
}

type CheckBoxFormProps = FormProps & {
  checkboxLabel?: string
  checked: boolean
  disabled?: boolean
  onChange: (ev: ChangeEvent<HTMLInputElement>) => void
}

type ColorPickerFormProps = FormProps & {
  color: ColorType
  disabled?: boolean
  onChange: (value: ColorType) => void
}

type InputGroupFormProps = FormProps & {
  value?: string
  addonText: string
  position?: 'append' | 'prepend'
  maxLength?: number
  disabled?: boolean
  onChange: (value: string) => void
  validations?: Array<(s: string | undefined) => string>
  onValidate?: (b: boolean) => void
  backgroundColor?: string
  onFocus?: () => void
  onBlur?: () => void
}

type RadioButtonFormProps<T> = FormProps & {
  value?: T
  items: Array<{ key: T; value: string; description?: string; component?: JSX.Element }>
  disabled?: boolean
  onChange: (item: { key: T; value: string }) => void
  direction?: 'vertical' | 'horizontal'
}

type UnlabeledRadioButtonFormProps<T> = {
  items: Array<{ key: T; value: string; description?: string; component?: JSX.Element }>
  value?: T
  disabled?: boolean
  onChange: (item: { key: T; value: string }) => void
  direction?: 'vertical' | 'horizontal'
  formText?: string | JSX.Element
  className?: string
}

export const InputFormat = ({
  label,
  value,
  labelSize = COLUMN_SIZES.SHORT,
  size = COLUMN_SIZES.LARGE,
  type = 'text',
  maxLength,
  disabled,
  placeholder,
  formText,
  className,
  autoComplete,
  onChange,
  validations,
  onValidate,
}: InputFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')
  const invalid = useCallback(
    (inputValue: string | undefined) => validations?.find(validation => validation(inputValue))?.(inputValue),
    [validations]
  )

  useEffect(() => {
    setErrorMessage(invalid(value))
  }, [invalid, value])

  const handleChange = (e: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => onChange?.(e.target.value)

  const id = useMemo(() => `id-${getUUID()}`, [])

  // 直接onValidateを実行すると親のrender中に子コンポーネントから状態を変えることになりwarningが出る
  useEffect(() => onValidate?.(!errorMessage), [onValidate, value, errorMessage])
  return (
    <>
      <div className={`row ${className}`}>
        <Label for={id} md={labelSize}>
          {label}
        </Label>
        <Col md={size} className="align-self-center">
          <Input
            value={value || ''}
            invalid={!!errorMessage}
            type={type}
            maxLength={maxLength}
            disabled={disabled}
            id={id}
            placeholder={placeholder}
            autoComplete={autoComplete}
            onChange={handleChange}
            onBlur={handleChange}
          />
          <FormText color="muted" hidden={!formText}>
            {formText}
          </FormText>
          <FormFeedback hidden={!errorMessage}>{errorMessage}</FormFeedback>
        </Col>
      </div>
    </>
  )
}

export const SelectBoxFormat = ({
  label,
  items,
  size = COLUMN_SIZES.LARGE,
  placeholder,
  formText,
  className,
  value = '',
  displayValue,
  disabled,
  onChange,
  validations,
  onValidate,
}: SelectBoxFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const invalid = useCallback(
    (inputValue: string | undefined) => validations?.find(validation => validation(inputValue))?.(inputValue),
    [validations]
  )

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    const str = event.target.value !== placeholder ? event.target.value : ''
    setErrorMessage(invalid(str))
  }

  const id = useMemo(() => `id-${getUUID()}`, [])
  const defaultValue = useMemo(() => {
    if (displayValue !== undefined) {
      return displayValue
    }
    const target = items.find(item => (item?.key ? item.key.toString() === value : item.value === value))
    return target ? target.value : ''
  }, [items, value, displayValue])

  useEffect(() => {
    setErrorMessage(invalid(defaultValue))
  }, [invalid, defaultValue])

  // 直接onValidateを実行すると親のrender中に子コンポーネントから状態を変えることになりwarningが出る
  useEffect(() => onValidate?.(!errorMessage), [onValidate, errorMessage])

  return (
    <>
      <div className={`row ${className}`}>
        <Label for={id} md={4}>
          {label}
        </Label>
        <Col md={size}>
          <SelectBox
            id={id}
            items={items}
            value={defaultValue}
            invalid={!!errorMessage}
            placeholder={placeholder}
            onSelect={onChange}
            onBlur={onBlur}
            disabled={disabled}
          />
          <div className="pt-1 text-muted font-x-small" style={{ display: formText ? '' : 'none' }}>
            {formText}
          </div>
          <div className="pt-1 text-danger font-x-small" style={{ display: errorMessage ? '' : 'none' }}>
            {errorMessage}
          </div>
        </Col>
      </div>
    </>
  )
}

export const CheckBoxFormat = ({
  label,
  checkboxLabel,
  checked,
  formText,
  className,
  disabled,
  onChange,
}: CheckBoxFormProps) => {
  const id = useMemo(() => `id-${getUUID()}`, [])
  return (
    <>
      <div className={`row ${className}`}>
        <Col md={4}>{label}</Col>
        <Col md={4}>
          <div className="form-check">
            <Input
              className="form-check-input"
              checked={checked}
              type="checkbox"
              disabled={disabled}
              id={id}
              onChange={onChange}
            />
            <Label className="form-check-label" for={id}>
              {checkboxLabel}
            </Label>
          </div>
          <FormText color="muted" hidden={!formText}>
            {formText}
          </FormText>
        </Col>
      </div>
    </>
  )
}

export const ColorPickerFormat = ({
  label,
  color,
  size = COLUMN_SIZES.LARGE,
  disabled,
  onChange,
}: ColorPickerFormProps) => {
  const id = useMemo(() => `id-${getUUID()}`, [])
  return (
    <Row>
      <Label for={id} md={4}>
        {label}
      </Label>
      <Col md={size} id={id}>
        <ColorPicker selected={color} disabled={disabled} onChange={onChange} />
      </Col>
    </Row>
  )
}

export const InputGroupFormat = ({
  label,
  labelSize = COLUMN_SIZES.SHORT,
  value,
  addonText,
  position = 'append',
  size = COLUMN_SIZES.SHORT,
  maxLength,
  formText,
  disabled,
  onChange,
  validations,
  onValidate,
  backgroundColor,
  onFocus,
  onBlur,
  className,
}: InputGroupFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')
  const invalid = useCallback(
    (inputValue: string | undefined) => validations?.find(validation => validation(inputValue))?.(inputValue),
    [validations]
  )

  useEffect(() => {
    setErrorMessage(invalid(value))
  }, [invalid, value])

  const handleChange = (e: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const id = useMemo(() => `id-${getUUID()}`, [])
  const style = { backgroundColor: backgroundColor || '#fff' }

  // 直接onValidateを実行すると親のrender中に子コンポーネントから状態を変えることになりwarningが出る
  useEffect(() => onValidate?.(!errorMessage), [onValidate, value, errorMessage])
  return (
    <>
      <div className={`row ${className}`}>
        <Label for={id} md={labelSize}>
          {label}
        </Label>
        <Col md={size}>
          <InputGroup>
            {position === 'prepend' && (
              <div className="input-group-prepend">
                <span className="input-group-text">{addonText}</span>
              </div>
            )}
            <Input
              id={id}
              style={style}
              value={value || ''}
              invalid={!!errorMessage}
              maxLength={maxLength}
              className="form-control"
              disabled={disabled}
              onChange={handleChange}
              onBlur={onBlur ? () => onBlur() : handleChange}
              onFocus={() => onFocus?.()}
            />
            {position === 'append' && (
              <div className={`input-group-text rounded-end bg-body-secondary`}>{addonText}</div>
            )}
            <FormFeedback hidden={!errorMessage}>{errorMessage}</FormFeedback>
          </InputGroup>
          <FormText color="muted" hidden={!formText}>
            {formText}
          </FormText>
        </Col>
      </div>
    </>
  )
}

export const UnlabeledRadioButtonFormat = <T,>({
  items,
  formText,
  className,
  value,
  disabled,
  onChange,
  direction = 'vertical',
}: UnlabeledRadioButtonFormProps<T>) => {
  const id = useMemo(() => `id-${getUUID()}`, [])

  return (
    <div className={className}>
      <div className={direction === 'horizontal' ? 'd-flex flex-wrap' : ''}>
        {items.map((item, index) => (
          <div key={index} className={`form-check ${direction === 'horizontal' ? 'me-3 mb-0' : ''}`}>
            <Input
              className="form-check-input"
              checked={value === item.key}
              type="radio"
              disabled={disabled}
              id={`${id}-${item.key}`}
              onChange={() => onChange(item)}
            />
            <Label className="form-check-label" for={`${id}-${item.key}`}>
              {item.value}
            </Label>
            {value === item.key && item.description && (
              <div className="text-muted font-x-small">{item.description}</div>
            )}
            {value === item.key && item.component}
          </div>
        ))}
      </div>
      <FormText color="muted" hidden={!formText}>
        {formText}
      </FormText>
    </div>
  )
}

export const RadioButtonFormat = <T,>({
  label,
  labelSize = COLUMN_SIZES.SHORT,
  items,
  size = COLUMN_SIZES.LARGE,
  formText,
  className,
  value,
  disabled,
  onChange,
  direction = 'vertical',
}: RadioButtonFormProps<T>) => {
  const id = useMemo(() => `id-${getUUID()}`, [])
  return (
    <>
      <div className={`row ${className}`}>
        <Label for={id} md={labelSize} className="pt-0">
          {label}
        </Label>
        <Col md={size}>
          <UnlabeledRadioButtonFormat
            items={items}
            value={value}
            disabled={disabled}
            onChange={onChange}
            direction={direction}
            formText={formText}
          />
        </Col>
      </div>
    </>
  )
}
